import React, { ComponentProps, ReactNode } from 'react';
import HBPopover from '../../HBPopover/HBPopover';
import styled from 'styled-components';
import { vars } from '../../../theme';
import HBText from '../../HBText/HBText';
import { customScrollBar } from '../../../styles/scrollbar';
import { STRUCTURE } from '../utils/structure';
import spacing from '../../../styles/spacing';
import HBClickable from '../../HBClickable/HBClickable';

interface IPopoverBaseProps {
    target: ReactNode;
    children: ReactNode;
    title: string;
    menu?: ReactNode;
    testIdName: string;
    enableInitialOpen?: boolean;
    onOpenChange?: (params: { isOpen: boolean }) => void;
    forceClose?: boolean;
    forceOpen?: boolean;
    onExitComplete?: () => void;
    disableAnimations?: boolean;
    targetContainerStyle?: React.CSSProperties;
    customStyle?: React.CSSProperties;
}

const NO_ANIMATIONS_VARIANTS = {
    hidden: {
        opacity: 0,
        transition: {
            duration: 0
        }
    },
    visible: {
        opacity: 1,
        transition: {
            duration: 0
        }
    }
};

const PopoverBase = ({
    target,
    children,
    title,
    menu,
    testIdName,
    enableInitialOpen,
    onOpenChange,
    forceClose,
    forceOpen,
    onExitComplete,
    targetContainerStyle,
    disableAnimations = false,
    customStyle = {}
}: IPopoverBaseProps) => {
    const animationVariants =
        (disableAnimations && NO_ANIMATIONS_VARIANTS) || null;

    return (
        <HBPopover
            customStyle={{
                width: 'fit-content',
                minWidth: STRUCTURE.popoverWidth,
                overflow: 'visible',
                padding: '0',
                ...customStyle
            }}
            offsetX={STRUCTURE.gapBetweenChipAndPopover}
            testIdName={testIdName}
            openTrigger="click"
            targetContainerStyle={targetContainerStyle}
            placement="bottom-start"
            enableInitialOpen={enableInitialOpen}
            shouldSupportClickOutside
            onOpenChange={onOpenChange}
            forceClose={forceClose}
            forceOpen={forceOpen}
            stopPropagationOnPopoverClick
            onExitComplete={onExitComplete}
            target={target}
            keepOpenOnHover={false}
            variants={animationVariants}
        >
            <StyledPopoverContent>
                <StyledHeader>
                    <HBText type="emphasis14" color="text.tertiary">
                        {title}
                    </HBText>
                    {menu}
                </StyledHeader>
                <StyledScrollableContent>{children}</StyledScrollableContent>
            </StyledPopoverContent>
        </HBPopover>
    );
};

// -------------------------------- Styles -------------------------------- //

const StyledPopoverContent = styled.div`
    max-height: ${STRUCTURE.maxPopoverHeight};
    display: flex;
    flex-direction: column;
    user-select: none;
`;

const StyledScrollableContent = styled.div`
    ${customScrollBar};
    overflow-y: auto;
    overflow-x: hidden;
    height: 100%;
    user-select: none;
`;

const StyledHeader = styled.div`
    display: flex;
    justify-content: space-between;
    padding: ${STRUCTURE.headerPadding};
    align-items: center;
    height: ${STRUCTURE.popoverHeaderHeight};
    box-sizing: content-box;
    width: inherit;
    overflow: visible;
`;

export const StyledFilterItem = styled.div`
    height: ${STRUCTURE.barHeight};
    display: flex;
    align-items: center;
    justify-content: center;
`;

const StyledListItemWrapper = styled.div`
    margin: ${STRUCTURE.itemMargin};
`;

export const ListItem = (
    props: ComponentProps<typeof HBClickable> & {
        $alignment: 'left' | 'spaced';
    }
) => {
    const { children, $alignment, ...restProps } = props;
    return (
        <StyledListItemWrapper>
            <StyledListItem {...restProps}>
                <StyledListRow $alignment={$alignment}>
                    {children}
                </StyledListRow>
            </StyledListItem>
        </StyledListItemWrapper>
    );
};

const StyledListRow = styled.div<{ $alignment: 'left' | 'spaced' }>`
    display: flex;
    align-items: center;
    justify-content: ${({ $alignment }) =>
        $alignment === 'left' ? 'flex-start' : 'space-between'};
    gap: ${spacing[8]};
    width: 218px; // TODO: ListItem currently doesn't work well with prefix/suffix combined with item ellipsis. This is a quick win, but we will need to fix this from the root.
    overflow: hidden;
`;

const StyledListItem = styled(HBClickable)`
    background: ${vars.colors.surface.item};
    display: flex;
    flex-direction: row;
    align-items: center;
    height: 42px;
    padding: 6px 12px;
    min-height: 42px;
    border-radius: ${vars.radius.xs};

    &:hover {
        background: ${vars.colors.surface.itemHover};
    }
`;

export default PopoverBase;
